// src/features/patient/care-treatment/utils/visitDays.ts

/**
 * Purpose : Visit days for the timeline: encounters on the same
 * date at the same facility / department are one row ("Prenatal visit +
 * Procedure"), each encounter a part of it. Also the row's title, clinician
 * line and document-native summary line, its note count, the filters,
 * previous / next, and the records "Around this visit". Pure functions.
 *
 * Contents :
 *
 * * interface(VisitDay) / function(groupVisitDays) / function(recordDay)
 * * function(visitDayTitle) / function(visitDayPlace) / function(visitDayMeta) / function(dayClinicians)
 * * function(summaryLines) / function(summaryLine) : "Diagnosis: …", "Reason: …", "Chief complaint: …" verbatim
 * * function(dayNotes) / function(noteCountText)
 * * const(VISIT_DAY_FILTERS) / function(filterOfKind) / function(dayMatches) / function(neighbourDays)
 * * type(DayContent) / function(dayHas) : the summary cards' "with doctors' notes" / "with results & orders"
 * * function(dayHasDetails) : whether opening the day shows more than its date, place and clinician
 * * function(dayNotesMissing) : whether some of the day's notes are on a page not loaded yet
 * * function(aroundVisit) : same department, within 14 days
 * * function(groupByYear) / function(groupByMonth)
 * * function(partNotes) / function(dayNoteSequence) : a part's notes; the day's notes in page order
 */

import type { Encounter, Note, VisitKind } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { formatWhen, isOrderNote, notesOfEncounters, sortNewestFirst } from "@/features/patient/care-treatment/utils/careTimeline";
import { recordTypeLabel, visitKind } from "@/features/patient/care-treatment/utils/careNaming";

// interface(VisitDay)
export interface VisitDay {
  /** The first part's id: stable while the day's parts don't change. */
  id: string;
  /** YYYY-MM-DD. */
  date: string;
  /** In time order. */
  parts: Encounter[];
}

const norm = (value: string | undefined) => (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
const dayOf = (e: Encounter) => e.start.slice(0, 10);

/** One person however the name was spelled: the server's grouping key, else the name itself. */
export const personKey = (name: string | undefined, key: string | undefined) => key || norm(name);
/** Time order within a day; a part with no time comes after the timed ones. */
const byStart = (a: Encounter, b: Encounter) =>
  dayOf(a).localeCompare(dayOf(b)) || Number(!a.hasTime) - Number(!b.hasTime) || a.start.localeCompare(b.start);

/** Where a record happened, for "same facility / department": "" when neither is known. */
function placeKey(e: Encounter): string {
  const facility = norm(e.facility);
  const department = norm(e.department);
  return facility || department ? `${facility}|${department}` : "";
}

// function(groupVisitDays)
/**
 * Visits grouped into days: same date AND same facility / department. A visit
 * with neither facility nor department stays on its own row; different
 * facilities on one date stay separate.
 *
 * @param encounters - All encounters (non-visits are ignored).
 * @returns Visit days, newest first; parts in time order.
 */
export function groupVisitDays(encounters: Encounter[]): VisitDay[] {
  const byKey = new Map<string, Encounter[]>();
  for (const e of encounters) {
    if (e.group !== "visit") continue;
    const place = placeKey(e);
    const key = place ? `${dayOf(e)}#${place}` : `id#${e.id}`;
    const list = byKey.get(key);
    if (list) list.push(e); else byKey.set(key, [e]);
  }
  const days = [...byKey.values()].map((parts) => {
    const sorted = [...parts].sort(byStart);
    return { id: sorted[0].id, date: dayOf(sorted[0]), parts: sorted };
  });
  return days.sort((a, b) => b.date.localeCompare(a.date) || b.parts[0].start.localeCompare(a.parts[0].start));
}

// function(recordDay)
/** A call, message or letter opened on its own page: a "day" of one part. */
export function recordDay(encounter: Encounter): VisitDay {
  return { id: encounter.id, date: dayOf(encounter), parts: [encounter] };
}

// function(visitDayTitle)
/** "Prenatal visit + Procedure + Pre-surgery prep"; a stay over several days adds its dates. */
export function visitDayTitle(day: VisitDay): string {
  const labels = [...new Set(day.parts.map(recordTypeLabel))];
  const title = labels.join(" + ");
  const only = day.parts.length === 1 ? day.parts[0] : null;
  if (only?.end && only.end.slice(0, 10) !== only.start.slice(0, 10)) {
    return `${title} · ${formatWhen(only.start, only.end, false).replace(/, \d{4}$/, "")}`;
  }
  return title;
}

// function(visitDayPlace)
/** The department, else the facility (first part that has one), else "". */
export function visitDayPlace(day: VisitDay): string {
  const withDept = day.parts.find((p) => p.department?.trim());
  if (withDept) return withDept.department!.trim();
  return day.parts.find((p) => p.facility?.trim())?.facility?.trim() ?? "";
}

// function(dayClinicians)
/** The day's clinicians in display form, each person once (by grouping key). */
export function dayClinicians(day: VisitDay): string[] {
  const seen = new Map<string, string>();
  for (const part of day.parts) {
    if (!part.practitioner?.trim()) continue;
    const key = personKey(part.practitioner, part.practitionerKey);
    if (!seen.has(key)) seen.set(key, part.practitioner.trim());
  }
  return [...seen.values()];
}

// function(visitDayMeta)
/** "Clinician · Department" for a timeline row; parts missing leave no gap. */
export function visitDayMeta(day: VisitDay): string {
  return [dayClinicians(day).join(", "), visitDayPlace(day)].filter(Boolean).join(" · ");
}

export interface SummaryLine {
  label: "Diagnosis" | "Reason" | "Chief complaint";
  text: string;
}

const unique = (values: (string | undefined | null)[]) =>
  [...new Set(values.map((v) => (v ?? "").trim()).filter(Boolean))];

// function(summaryLines)
/**
 * The visit summary exactly as the record wrote it: visit diagnoses, the
 * appointment reason, the chief complaint. Absent fields are left out.
 *
 * @param day - The visit day.
 * @returns Its summary lines, diagnoses first.
 */
export function summaryLines(day: VisitDay): SummaryLine[] {
  const diagnoses = unique(day.parts.flatMap((p) => (p.visitDiagnoses ?? []).map((d) => d.text)));
  const reasons = unique(day.parts.map((p) => p.appointmentReason?.text || p.reason));
  const complaints = unique(day.parts.flatMap((p) => (p.chiefComplaints ?? []).map((c) => c.text)));
  const lines: SummaryLine[] = [];
  if (diagnoses.length) lines.push({ label: "Diagnosis", text: diagnoses.join("; ") });
  if (reasons.length) lines.push({ label: "Reason", text: reasons.join("; ") });
  if (complaints.length) lines.push({ label: "Chief complaint", text: complaints.join("; ") });
  return lines;
}

// function(summaryLine)
/** The row's one line: Diagnosis, else Reason, else Chief complaint; null when none. */
export function summaryLine(day: VisitDay): SummaryLine | null {
  return summaryLines(day)[0] ?? null;
}

// function(dayNotes)
/** The notes linked to any part of the day. */
export function dayNotes(day: VisitDay, notes: Note[]): Note[] {
  return notesOfEncounters(day.parts, notes);
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

// function(noteCountText)
/** "2 notes", else "2 reports" when there are only results, else "". */
export function noteCountText(notes: Note[]): string {
  const clinical = notes.filter((n) => n.category !== "report" && !isOrderNote(n)).length;
  if (clinical > 0) return plural(clinical, "note");
  return notes.length > 0 ? plural(notes.length, "report") : "";
}

// const(VISIT_DAY_FILTERS)
export type VisitDayFilter =
  "all" | "office" | "pp" | "procedure" | "hospital" | "imaging" | "emergency" | "telehealth" | "other";

export const VISIT_DAY_FILTERS: { id: VisitDayFilter; label: string; kinds: VisitKind[] }[] = [
  { id: "all", label: "All", kinds: [] },
  { id: "office", label: "Office visits", kinds: ["office"] },
  { id: "pp", label: "Prenatal & postpartum", kinds: ["prenatal", "postpartum"] },
  { id: "procedure", label: "Procedures", kinds: ["procedure"] },
  { id: "hospital", label: "Hospital stays", kinds: ["hospital"] },
  { id: "imaging", label: "Imaging", kinds: ["imaging"] },
  { id: "emergency", label: "Emergency", kinds: ["emergency"] },
  { id: "telehealth", label: "Telehealth", kinds: ["telehealth"] },
  // Days whose main type is none of the above: the chips then add up to All.
  { id: "other", label: "Other", kinds: [] },
];

// function(filterOfKind)
/**
 * The one type filter a visit belongs to ("other" when none lists its kind).
 * The type counts count visits, each under its own type, so they add up to
 * All and no type is hidden; a day with a prenatal visit and a procedure is
 * listed under both (2026-10-29: counting each day under one main type
 * hid office visits and procedures in a pregnancy chart).
 */
export function filterOfKind(kind: VisitKind): VisitDayFilter {
  return VISIT_DAY_FILTERS.find((f) => f.kinds.includes(kind))?.id ?? "other";
}

// function(dayMatches)
/**
 * Whether a day passes the type filter (any part of that kind), the
 * clinician filter (a part's clinician or a note's author, by grouping key)
 * and, from a summary card, what it must contain.
 */
export function dayMatches(
  day: VisitDay, notes: Note[], filter: VisitDayFilter, clinicianKey: string | null, content: DayContent | null = null,
): boolean {
  if (content && !dayHas(day, notes, content)) return false;
  if (filter !== "all" && !day.parts.some((p) => filterOfKind(visitKind(p)) === filter)) return false;
  if (clinicianKey) {
    const byPart = day.parts.some((p) => personKey(p.practitioner, p.practitionerKey) === clinicianKey);
    const byNote = dayNotes(day, notes).some((n) => personKey(n.author, n.authorKey) === clinicianKey);
    if (!byPart && !byNote) return false;
  }
  return true;
}

// type(DayContent) / function(dayHas)
/** What a summary card narrows the timeline to: days with doctors' notes, or with results & orders. */
export type DayContent = "notes" | "results";

/** Whether the day has a clinical note ("notes") or a result / order ("results"). */
export function dayHas(day: VisitDay, notes: Note[], content: DayContent): boolean {
  const own = dayNotes(day, notes);
  return content === "notes"
    ? own.some((n) => n.category !== "report" && !isOrderNote(n))
    : own.some((n) => n.category === "report" || isOrderNote(n));
}

// function(dayHasDetails)
/**
 * Whether a visit day has anything to open beyond its date, place and
 * clinician: a note, result or order, a diagnosis, vitals, medicines or
 * procedures, its own record text, a document, the visit summary as printed,
 * or a next appointment. Without any, the visit page would be empty, so the
 * row isn't clickable and says so.
 *
 * @param dayNotesList - The day's own notes (dayNotes).
 */
export function dayHasDetails(day: VisitDay, dayNotesList: Note[]): boolean {
  if (dayNotesList.length > 0) return true;
  return day.parts.some((p) => Boolean(
    // Notes on a page not loaded yet still count (the server's counts are over every note).
    p.noteCount || p.reportCount
    || p.diagnoses?.length || p.vitals?.length
    || Object.values(p.orders ?? {}).some((items) => items?.length)
    || p.recordText?.text?.trim() || p.otherDocuments?.length
    || p.chiefComplaints?.length || p.visitDiagnoses?.length || p.appointmentReason?.text?.trim()
    || p.nextAppointment,
  ));
}

// function(neighbourDays)
/** The days just before (older) and after (newer) this one, in the filtered list. */
export function neighbourDays(days: VisitDay[], id: string): { previous: VisitDay | null; next: VisitDay | null } {
  const at = days.findIndex((d) => d.id === id);
  if (at < 0) return { previous: null, next: null };
  return { previous: days[at + 1] ?? null, next: days[at - 1] ?? null };
}

const AROUND_DAYS = 14;
const toDay = (iso: string) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10));

// function(aroundVisit)
/**
 * Calls, messages and other records from the same department (else the same
 * facility) within 14 days before or after the day. Only "nearby"; never
 * "related to".
 *
 * @param day - The visit day.
 * @param encounters - Everything.
 * @returns Those records, nearest date first.
 */
export function aroundVisit(day: VisitDay, encounters: Encounter[]): Encounter[] {
  const department = norm(visitDayPlaceOf(day, "department"));
  const facility = norm(visitDayPlaceOf(day, "facility"));
  if (!department && !facility) return [];
  const at = toDay(day.date);
  return encounters
    .filter((e) => e.group === "non_visit")
    .filter((e) => (department ? norm(e.department) === department : norm(e.facility) === facility))
    .filter((e) => Math.abs(toDay(dayOf(e)) - at) <= AROUND_DAYS * 86_400_000)
    .sort((a, b) => Math.abs(toDay(dayOf(a)) - at) - Math.abs(toDay(dayOf(b)) - at));
}

function visitDayPlaceOf(day: VisitDay, field: "department" | "facility"): string {
  return day.parts.find((p) => p[field]?.trim())?.[field] ?? "";
}

// function(groupByYear)
export function groupByYear(days: VisitDay[]): { year: string; days: VisitDay[] }[] {
  const groups: { year: string; days: VisitDay[] }[] = [];
  for (const day of days) {
    const year = day.date.slice(0, 4);
    const last = groups[groups.length - 1];
    if (last?.year === year) last.days.push(day); else groups.push({ year, days: [day] });
  }
  return groups;
}

// function(groupByMonth)
/** Records newest first, grouped by "Jun 2017". */
export function groupByMonth(records: Encounter[]): { month: string; records: Encounter[] }[] {
  const groups: { month: string; records: Encounter[] }[] = [];
  for (const record of sortNewestFirst(records)) {
    const [y, m] = [+record.start.slice(0, 4), +record.start.slice(5, 7)];
    const month = Number.isNaN(y) || Number.isNaN(m)
      ? ""
      : new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
    const last = groups[groups.length - 1];
    if (last?.month === month) last.records.push(record); else groups.push({ month, records: [record] });
  }
  return groups;
}

// function(partNotes)
/** One part's notes in time order: clinical notes, then its results & orders. */
export function partNotes(part: Encounter, notes: Note[]): { clinical: Note[]; orders: Note[] } {
  const own = notesOfEncounters([part], notes).sort((a, b) => a.date.localeCompare(b.date));
  return { clinical: own.filter((n) => !isOrderNote(n)), orders: own.filter(isOrderNote) };
}

// function(dayNoteSequence)
/** Every note of the day in the order the visit page shows them: previous / next note walks this. */
export function dayNoteSequence(day: VisitDay, notes: Note[]): Note[] {
  const seen = new Set<string>();
  return day.parts.flatMap((part) => {
    const { clinical, orders } = partNotes(part, notes);
    return [...clinical, ...orders].filter((n) => !seen.has(n.id) && Boolean(seen.add(n.id)));
  });
}

// function(dayNotesMissing)
/** The server counts more notes for this day's visits than are loaded (they're on an older page). */
export function dayNotesMissing(day: VisitDay, dayNotesList: Note[]): boolean {
  const counted = day.parts.reduce((n, p) => n + (p.noteCount ?? 0) + (p.reportCount ?? 0), 0);
  return counted > dayNotesList.length;
}

